import { type MouseEventHandler, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch } from '@witmetrics/data-store';
import type { ProjectStatusId } from '@witmetrics/utils';
import { PROJECT_STATUS_COLORS } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import ProjectStatusCircle from '@/components/ProjectStatusCircle';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import ProjectStatusMenu from '@/menus/ProjectStatusMenu';
import { classes, getStatusLabel, updateProjectStatus } from './utils';

export type ProjectStatusProps = {
  className?: string;
  labelClassName?: string;
  projectId: number;
  projectStatusId: ProjectStatusId;
};

export default function ProjectStatus({
  className,
  labelClassName,
  projectId,
  projectStatusId,
}: ProjectStatusProps) {
  const statusRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  };

  const handleStatusChange = async (statusId: ProjectStatusId) => {
    try {
      toggleSaving(true);
      toggleMenu(false);
      dispatch.addProjects([await updateProjectStatus(projectId, statusId)]);
      toggleSaving(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error updating status');
    }
  };

  return (
    <>
      <button
        ref={statusRef}
        className={twMerge(classes.button, className)}
        onClick={handleClick}>
        {isSaving ? (
          <>
            <CircularProgress
              className={classes.projectStatusCircle}
              size={14}
              color="inherit"
            />
            <span>
              Saving
              <LoopingEllipsis />
            </span>
          </>
        ) : (
          <>
            <ProjectStatusCircle
              className={classes.projectStatusCircle}
              projectStatusId={projectStatusId}
              size={14}
            />
            <span
              className={twMerge(
                classes.label,
                PROJECT_STATUS_COLORS[projectStatusId].text,
                labelClassName
              )}>
              {getStatusLabel(projectStatusId)}
            </span>
          </>
        )}
      </button>
      {isMenuOpen && (
        <ProjectStatusMenu
          anchorTo={statusRef.current}
          open
          onChange={handleStatusChange}
          onClose={() => toggleMenu(false)}
        />
      )}
    </>
  );
}
